/**
 * This file contains some re-implementations of internal event tracking types.
 *
 * This is done so we can override the appKey, and send both internal and external events
 * fromm the same app, with differing app keys.
 *
 * The event tracking library doesn't allow this, since the internal/external split is
 * delineated by the app events are sent from. We're overriding it here because the demo form
 * used to be on an external app; this version is a version that's been migrated to an
 * internal app, but we want to emit the old events for the sake of analytics continuity.
 *
 * I don't recommend that you copy this pattern.
 */

import {TrackingEvent, TrackingContext} from '@udemy/event-tracking';

const EXTERNAL_EVENT_APP_KEY = 'web_business';

class ExternalPage {
    constructor(public trackingId: string) {}
}

class ExternalClientHeader {
    constructor(
        public appKey: string,
        public visitorUuid: string,
        public sessionId: string,
        public page: ExternalPage,
    ) {}
}

class ExternalClientEvent extends TrackingEvent {
    clientHeader?: ExternalClientHeader;

    processContext(context: TrackingContext) {
        this.clientHeader = new ExternalClientHeader(
            EXTERNAL_EVENT_APP_KEY,
            context.visitorUuid,
            context.sessionId,
            new ExternalPage(context.pageTrackingId),
        );
    }
}

export class ExternalPageViewEvent extends ExternalClientEvent {
    constructor() {
        super('ExternalPageViewEvent');
    }

    interfaceLanguage?: string;
    url?: string;
    referrer?: string | null;
    timezoneOffset?: number;

    processContext(context: TrackingContext) {
        super.processContext(context);
        this.interfaceLanguage = context.appLanguage;
        this.url = context.url;
        this.referrer = context.referrer;
        this.timezoneOffset = context.timezoneOffset;
    }
}

export class ExternalLeadFormSubmitEvent extends ExternalClientEvent {
    formId: string;
    isSuccessful: boolean;
    failureReason?: string;

    constructor({
        formId,
        isSuccessful,
        failureReason,
    }: {
        formId: string;
        isSuccessful: boolean;
        failureReason?: string;
    }) {
        super('ExternalLeadFormSubmitEvent');
        this.formId = formId;
        this.isSuccessful = isSuccessful;
        this.failureReason = failureReason;
    }
}
